<script lang="ts" setup>
import { computed, onBeforeUnmount } from 'vue'
import { format } from 'quasar'
import { storeToRefs } from 'pinia'
import { useIncidentSubmissionStore } from '../../stores/incident-submission'
import { formatIncidentDateTime } from '../../utils/format'
import { useFileUploadStore } from '../../stores/file-upload'
import { crashTypeAndAddress } from '../../utils/recorded-crash-text'

const { humanStorageSize } = format
const incidentStore = useIncidentSubmissionStore()
const fileUploadStore = useFileUploadStore()
const { nearbyCrashes, selectedCrashId } = storeToRefs(incidentStore)

const linkedCrashText = computed(() => {
  const crash = nearbyCrashes.value.find((c) => c.id === selectedCrashId.value)
  return crash ? crashTypeAndAddress(crash) : ''
})

// --- Data Formatting ---
const formattedIncidentDate = computed(() =>
  formatIncidentDateTime(
    incidentStore.incidentForm.incidentDateTime,
    incidentStore.incidentForm.incidentTimezone
  )
)

// --- Memory-Safe Object URLs ---
const objectUrlCache = new Map<string, string>()

onBeforeUnmount(() => {
  objectUrlCache.forEach((url) => URL.revokeObjectURL(url))
  objectUrlCache.clear()
})

// --- Play / pause preview
function playPreview(e: Event) {
  const vid = e.target as HTMLVideoElement
  vid.play().catch(() => {})
}

function pausePreview(e: Event) {
  const vid = e.target as HTMLVideoElement
  vid.pause()
}
</script>

<template>
  <div class="row q-col-gutter-lg">
    <div class="col-12">
      <h6 class="text-h6 q-mt-none q-mb-xs text-blue-grey-9" tabindex="-1" data-step-heading>
        Review & submit
      </h6>
      <p class="text-body2 text-blue-grey-8">
        Please verify your incident details before final submission.
      </p>
    </div>

    <div class="col-12 col-md-6 flex column q-gutter-y-md">
      <q-card flat bordered class="bg-white">
        <q-card-section>
          <div class="row justify-between items-center q-mb-sm">
            <div class="text-subtitle1 text-weight-bold text-blue-grey-9">1. Device</div>
            <q-btn
              flat
              dense
              color="blue-8"
              label="Edit"
              class="text-caption text-weight-bold"
              @click="incidentStore.jumpToStep('start')"
            />
          </div>
          <div class="text-body2 text-blue-grey-8 q-mb-md">
            {{ incidentStore.incidentForm.deviceVendor || 'Generic / Other Dashcam' }}
          </div>

          <q-separator class="q-my-md" />

          <div class="row justify-between items-center q-mb-sm">
            <div class="text-subtitle1 text-weight-bold text-blue-grey-9">3. Details</div>
            <q-btn
              flat
              dense
              color="blue-8"
              label="Edit"
              class="text-caption text-weight-bold"
              @click="incidentStore.jumpToStep('incident-details')"
            />
          </div>
          <div class="text-body2 text-blue-grey-8 q-mb-md">
            <div class="q-mb-xs">
              <strong>Type:</strong>
              {{ incidentStore.incidentForm.incidentReportType || 'Not specified' }}
            </div>
            <div class="q-mb-xs"><strong>Time:</strong> {{ formattedIncidentDate }}</div>
            <div class="q-mb-xs">
              <strong>Address:</strong>
              {{ incidentStore.incidentForm.address?.rawInput || 'No address provided' }}
            </div>
            <div class="q-mt-sm bg-grey-1 q-pa-sm border-flat rounded-borders text-caption">
              <strong>Description:</strong><br />
              {{
                incidentStore.incidentForm.userSubmittedIncidentDescription ||
                'No description provided.'
              }}
            </div>
          </div>

          <q-separator class="q-my-md" />

          <div class="row justify-between items-center q-mb-sm">
            <div class="text-subtitle1 text-weight-bold text-blue-grey-9">4. Location</div>
            <q-btn
              flat
              dense
              color="blue-8"
              label="Edit"
              class="text-caption text-weight-bold"
              @click="incidentStore.jumpToStep('refine-location')"
            />
          </div>
          <div class="row items-center justify-between text-body2 text-blue-grey-8">
            <div>
              <p>
                <strong>Location:&nbsp;</strong
                >{{ incidentStore.incidentForm.address?.formattedAddress || 'No address provided' }}
              </p>
              <p></p>
              <div v-if="incidentStore.incidentForm.localLatitude">
                LAT:&nbsp;<strong>{{ incidentStore.incidentForm.localLatitude }}</strong
                ><br />
                LONG:&nbsp;<strong>{{ incidentStore.incidentForm.localLongitude }}</strong>
              </div>
              <div v-else class="text-negative">Location not pinned.</div>
            </div>
          </div>

          <template v-if="linkedCrashText">
            <q-separator class="q-my-md" />
            <div
              class="text-body2 bg-amber-1 text-grey-10 q-pa-sm rounded-borders"
              data-testid="review-linked-crash"
            >
              <q-icon name="mdi-alert-circle-outline" color="warning" class="q-mr-xs" />
              <strong>Linked recorded crash:</strong> {{ linkedCrashText }} (shown after review)
              <q-btn
                flat
                dense
                color="blue-8"
                label="Change"
                class="text-caption text-weight-bold q-ml-sm"
                @click="incidentStore.jumpToStep('recorded-crash')"
              />
            </div>
          </template>
        </q-card-section>
      </q-card>
    </div>

    <div class="col-12 col-md-6 flex column">
      <q-card flat bordered class="bg-white full-height flex column">
        <q-card-section class="q-pb-none">
          <div class="row justify-between items-center q-mb-sm">
            <div class="text-subtitle1 text-weight-bold text-blue-grey-9">2. Uploads</div>
            <q-btn
              flat
              dense
              color="blue-8"
              label="Edit"
              class="text-caption text-weight-bold"
              @click="incidentStore.jumpToStep('media-upload')"
            />
          </div>
        </q-card-section>

        <q-card-section class="flex-grow">
          <div
            v-if="!fileUploadStore.hasFiles"
            class="flex flex-center full-height bg-grey-1 border-flat rounded-borders q-pa-lg text-center text-blue-grey-5"
          >
            <div>
              <q-icon name="mdi-image-off" size="xl" class="q-mb-sm" />
              <div>No media uploaded for this incident.</div>
            </div>
          </div>

          <div v-else class="row q-col-gutter-md">
            <div
              class="col-12 col-sm-6"
              v-for="file in fileUploadStore.userFileList"
              :key="file.internalId"
            >
              <q-card
                flat
                bordered
                class="overflow-hidden"
                :class="file.isPrimary ? 'bg-blue-1 border-blue' : 'bg-grey-1'"
              >
                <q-responsive :ratio="16 / 9">
                  <q-img
                    v-if="file.uploadMimeType?.startsWith('image/')"
                    :src="file.localThumbnailUrl"
                    :alt="`Preview of ${file.uploadFileName}`"
                    fit="cover"
                  />

                  <video
                    v-else-if="file.localMediaUrl"
                    :src="file.localMediaUrl"
                    :poster="file.localThumbnailUrl"
                    muted
                    loop
                    playsinline
                    class="full-width full-height block"
                    style="object-fit: cover; cursor: pointer"
                    @mouseenter="playPreview"
                    @mouseleave="pausePreview"
                  ></video>
                </q-responsive>
                <q-card-section class="q-pa-sm">
                  <div class="text-caption text-weight-bold ellipsis">
                    {{ file.uploadFileName }}
                  </div>
                  <div class="row justify-between text-caption text-blue-grey-7 q-mt-xs">
                    <span>{{ humanStorageSize(file.uploadFileSize || 0) }}</span>
                    <q-icon v-if="file.isPrimary" name="mdi-star" color="blue-8" size="sm" />
                  </div>
                  <div
                    class="text-caption text-weight-bold q-mt-xs"
                    :class="
                      file.hasError
                        ? 'text-negative'
                        : file.isUploaded
                          ? 'text-green-10'
                          : 'text-blue-9'
                    "
                  >
                    {{
                      file.hasError
                        ? 'Upload Failed'
                        : file.isUploaded
                          ? 'Uploaded securely'
                          : 'Upload pending...'
                    }}
                  </div>
                </q-card-section>
              </q-card>
            </div>
          </div>
        </q-card-section>
      </q-card>
    </div>

    <div class="col-12 q-mt-md">
      <q-banner rounded class="bg-blue-grey-1 border-deep-asphalt text-blue-grey-9">
        <template v-slot:avatar>
          <q-icon name="mdi-shield-check" color="blue-grey-6" />
        </template>
        <div class="text-subtitle2 text-weight-bold">Ready to submit?</div>
        <div class="text-body2">
          By clicking "Submit Report" below, you confirm the following:
          <ul>
            <li>The information provided is accurate.</li>
            <li>
              The uploaded media meets the terms set out in the
              <a href="https://roadfootage.com/terms" target="_blank" rel="noopener noreferrer"
                >RoadFootage Terms of Use.</a
              >
            </li>
            <li>
              The uploaded media was recorded in a public location and is conformant to the
              <a href="https://roadfootage.com/privacy" target="_blank" rel="noopener noreferrer"
                >RoadFootage privacy policy.</a
              >
            </li>
            <li>
              You are the rightful copyright owner or have obtained permission from the copyright
              owner to use the media.
            </li>
          </ul>
          Our moderation team will process the incident shortly.
        </div>
      </q-banner>
    </div>
  </div>
</template>

<style scoped>
.border-flat {
  border: 1px solid var(--q-blue-grey-2);
}

.border-deep-asphalt {
  border: 1px solid var(--q-blue-grey-9);
}

.border-blue {
  border: 2px solid var(--q-blue-8);
}

.flex-grow {
  flex-grow: 1;
}
</style>
